import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, uploadImage, uploadFile } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { EDITABLE_CATEGORIES, getCategory } from "../../lib/categories";
import RichTextEditor from "../../components/RichTextEditor";
import { ArrowLeft, Save, Send, Eye, Star, Flame, Search as SearchIcon, Upload, Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const EMPTY = {
  title: "", summary: "", content: "", category: "actualites", subcategory: "",
  tags: [], author: "", cover_image: "", images: [], videos: [], status: "draft",
  is_featured: false, is_popular: false, allow_comments: true, published_at: "",
  slug: "", meta_title: "", meta_description: "",
};

const slugify = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/[\s_-]+/g, "-").slice(0, 80);

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPublish = user.role === "admin";
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingVideos, setUploadingVideos] = useState(false);

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file);
      set("cover_image", url);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  const onGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      set("images", [...(form.images || []), ...urls]);
      toast.success(`${urls.length} photo(s) ajoutée(s) à la galerie`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploadingGallery(false);
      e.target.value = "";
    }
  };

  const onVideosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingVideos(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      set("videos", [...(form.videos || []), ...urls]);
      toast.success(`${urls.length} vidéo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploadingVideos(false);
      e.target.value = "";
    }
  };
  const [loaded, setLoaded] = useState(!id);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    document.title = id ? "Modifier l'article — Admin" : "Nouvel article — Admin";
    if (id) {
      api.get(`/admin/articles/${id}`).then((r) => {
        const a = r.data;
        setForm({ ...EMPTY, ...a, published_at: toLocalInput(a.published_at) });
        setSlugTouched(true);
        setLoaded(true);
      }).catch(() => { toast.error("Article introuvable"); navigate("/admin"); });
    } else {
      setForm({ ...EMPTY });
    }
  }, [id]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitle = (v) => {
    set("title", v);
    if (!slugTouched) set("slug", slugify(v));
  };

  const addTag = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      if (!form.tags.includes(tagInput.trim())) set("tags", [...form.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const cat = getCategory(form.category);

  const save = async (status) => {
    if (!form.title.trim()) { toast.error("Le titre est obligatoire"); return; }
    setSaving(true);
    const payload = {
      ...form,
      status,
      published_at: form.published_at ? new Date(form.published_at).toISOString() : null,
      subcategory: form.subcategory || null,
    };
    if (status === "scheduled" && !payload.published_at) {
      toast.error("Choisissez une date de programmation");
      setSaving(false);
      return;
    }
    try {
      if (id) {
        await api.put(`/admin/articles/${id}`, payload);
        toast.success("Article mis à jour");
      } else {
        const r = await api.post("/admin/articles", payload);
        toast.success("Article créé");
        navigate(`/admin/articles/${r.data.id}/edit`, { replace: true });
      }
      if (status === "published") toast.success("Article publié ✔");
      if (status === "pending") toast.success("Article soumis à validation ✔");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="p-8 meta-label text-slate-400">Chargement…</div>;

  return (
    <div className="p-5 sm:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-2 meta-label text-slate-500 hover:text-[#0F172A]" data-testid="editor-back">
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => save("draft")} disabled={saving} data-testid="save-draft-button"
            className="inline-flex items-center gap-2 border border-[#E2E8F0] bg-white px-4 py-2.5 rounded-full font-sub text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
            <Save size={15} /> Brouillon
          </button>
          {!canPublish && (
            <button onClick={() => save("pending")} disabled={saving} data-testid="submit-review-button"
              className="inline-flex items-center gap-2 bg-[#EA580C] text-white px-5 py-2.5 rounded-full font-sub text-sm font-semibold hover:bg-[#c2410c] disabled:opacity-50">
              <Send size={15} /> Soumettre à validation
            </button>
          )}
          {canPublish && (
            <>
              <button onClick={() => save("scheduled")} disabled={saving} data-testid="schedule-button"
                className="inline-flex items-center gap-2 border border-amber-300 text-amber-700 bg-amber-50 px-4 py-2.5 rounded-full font-sub text-sm font-semibold hover:bg-amber-100 disabled:opacity-50">
                <Eye size={15} /> Programmer
              </button>
              <button onClick={() => save("published")} disabled={saving} data-testid="publish-button"
                className="inline-flex items-center gap-2 bg-[#E11D48] text-white px-5 py-2.5 rounded-full font-sub text-sm font-semibold hover:bg-[#be123c] disabled:opacity-50">
                <Send size={15} /> Publier
              </button>
            </>
          )}
        </div>
      </div>

      <h1 className="font-headline text-3xl font-black text-[#0F172A] mb-6">{id ? "Modifier l'article" : "Nouvel article"}</h1>

      <div className="flex gap-2 mb-5 border-b border-[#E2E8F0]">
        {[["content", "Contenu"], ["settings", "Réglages"], ["seo", "SEO"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`editor-tab-${v}`}
            className={`font-sub text-sm font-semibold px-4 py-2.5 border-b-2 -mb-px ${tab === v ? "border-[#E11D48] text-[#0F172A]" : "border-transparent text-slate-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "content" && (
        <div className="space-y-5">
          <div>
            <Label className="meta-label text-slate-500">Titre *</Label>
            <Input value={form.title} onChange={(e) => onTitle(e.target.value)} data-testid="editor-title"
              placeholder="Titre de l'article" className="mt-1.5 font-headline text-xl h-auto py-3" />
          </div>
          <div>
            <Label className="meta-label text-slate-500">Résumé / Chapô</Label>
            <Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="editor-summary"
              placeholder="Résumé qui apparaît dans les listes et le partage…" rows={2} className="mt-1.5 font-body" />
          </div>
          <div>
            <Label className="meta-label text-slate-500 mb-1.5 block">Contenu</Label>
            <RichTextEditor value={form.content} onChange={(v) => set("content", v)} />
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-5 max-w-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="meta-label text-slate-500">Rubrique</Label>
              <Select value={form.category} onValueChange={(v) => { set("category", v); set("subcategory", ""); }}>
                <SelectTrigger className="mt-1.5" data-testid="editor-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EDITABLE_CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="meta-label text-slate-500">Sous-rubrique</Label>
              <Select value={form.subcategory || "none"} onValueChange={(v) => set("subcategory", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="editor-subcategory"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {cat.subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="meta-label text-slate-500">Signature de l'auteur (optionnel)</Label>
            <Input value={form.author} onChange={(e) => set("author", e.target.value)} data-testid="editor-author" className="mt-1.5" placeholder="Nom à afficher en bas de l'article" />
            <p className="meta-label text-slate-400 mt-1">Laissez vide pour ne pas afficher de signature.</p>
          </div>

          <div>
            <Label className="meta-label text-slate-500">Image principale</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={form.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} data-testid="editor-cover" className="flex-1" placeholder="Coller une URL ou téléverser une photo" />
              <label className="inline-flex items-center gap-2 border border-[#E2E8F0] bg-white px-4 rounded-md font-sub text-sm font-semibold cursor-pointer hover:bg-slate-50 whitespace-nowrap" data-testid="cover-upload-label">
                {uploadingCover ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Téléverser
                <input type="file" accept="image/*" className="hidden" data-testid="cover-upload-input" onChange={onCoverUpload} disabled={uploadingCover} />
              </label>
            </div>
            {form.cover_image && <img src={form.cover_image} alt="" className="mt-3 w-full max-w-sm rounded-lg" />}
          </div>

          <div>
            <Label className="meta-label text-slate-500">Galerie de photos (plusieurs images)</Label>
            <label className="mt-1.5 flex items-center gap-2 border border-dashed border-[#CBD5E1] bg-white px-4 py-3 rounded-md font-sub text-sm font-semibold cursor-pointer hover:bg-slate-50 w-fit" data-testid="gallery-upload-label">
              {uploadingGallery ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Ajouter des photos
              <input type="file" accept="image/*" multiple className="hidden" data-testid="gallery-upload-input" onChange={onGalleryUpload} disabled={uploadingGallery} />
            </label>
            {form.images?.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                {form.images.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt="" className="w-full h-20 object-cover rounded" />
                    <button type="button" onClick={() => set("images", form.images.filter((_, j) => j !== i))}
                      data-testid={`gallery-remove-${i}`}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="meta-label text-slate-500">Vidéos (plusieurs possible)</Label>
            <label className="mt-1.5 flex items-center gap-2 border border-dashed border-[#CBD5E1] bg-white px-4 py-3 rounded-md font-sub text-sm font-semibold cursor-pointer hover:bg-slate-50 w-fit" data-testid="videos-upload-label">
              {uploadingVideos ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />} Ajouter des vidéos
              <input type="file" accept="video/*" multiple className="hidden" data-testid="videos-upload-input" onChange={onVideosUpload} disabled={uploadingVideos} />
            </label>
            {form.videos?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {form.videos.map((url, i) => (
                  <div key={i} className="relative group">
                    <video src={url} controls className="w-full rounded bg-black" />
                    <button type="button" onClick={() => set("videos", form.videos.filter((_, j) => j !== i))}
                      data-testid={`video-remove-${i}`}
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="meta-label text-slate-500">Mots-clés / Tags</Label>
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={addTag} data-testid="editor-tags"
              className="mt-1.5" placeholder="Tapez un mot-clé puis Entrée" />
            <div className="flex flex-wrap gap-2 mt-2">
              {form.tags.map((t) => (
                <span key={t} className="meta-label bg-slate-100 px-2.5 py-1 rounded-full flex items-center gap-1.5">
                  {t}<button onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="text-slate-400 hover:text-rose-500">×</button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <Label className="meta-label text-slate-500">Date de publication</Label>
            <Input type="datetime-local" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} data-testid="editor-publish-date" className="mt-1.5 max-w-xs" />
            <p className="font-body text-xs text-slate-400 mt-1">Pour programmer une publication future, choisissez une date puis cliquez sur « Programmer ».</p>
          </div>

          <div className="space-y-3 pt-2">
            <label className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg">
              <span className="flex items-center gap-2 font-sub text-sm"><Star size={16} className="text-[#E11D48]" /> À la une</span>
              <Switch checked={form.is_featured} onCheckedChange={(v) => set("is_featured", v)} data-testid="editor-featured" />
            </label>
            <label className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg">
              <span className="flex items-center gap-2 font-sub text-sm"><Flame size={16} className="text-[#EA580C]" /> Article populaire</span>
              <Switch checked={form.is_popular} onCheckedChange={(v) => set("is_popular", v)} data-testid="editor-popular" />
            </label>
            <label className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg">
              <span className="font-sub text-sm">Autoriser les commentaires</span>
              <Switch checked={form.allow_comments} onCheckedChange={(v) => set("allow_comments", v)} data-testid="editor-comments" />
            </label>
          </div>
        </div>
      )}

      {tab === "seo" && (
        <div className="space-y-5 max-w-2xl">
          <div>
            <Label className="meta-label text-slate-500">Slug URL</Label>
            <div className="flex items-center gap-1 mt-1.5">
              <span className="meta-label text-slate-400">/{cat.slug}/</span>
              <Input value={form.slug} onChange={(e) => { set("slug", slugify(e.target.value)); setSlugTouched(true); }} data-testid="editor-slug" placeholder="titre-de-l-article" />
            </div>
          </div>
          <div>
            <Label className="meta-label text-slate-500">Meta title</Label>
            <Input value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} data-testid="editor-meta-title" className="mt-1.5" placeholder="Titre pour Google (60 car.)" />
          </div>
          <div>
            <Label className="meta-label text-slate-500">Meta description</Label>
            <Textarea value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid="editor-meta-description" rows={3} className="mt-1.5" placeholder="Description pour les moteurs de recherche (160 car.)" />
          </div>
          <div className="bg-slate-50 border border-[#E2E8F0] rounded-lg p-4">
            <span className="meta-label text-slate-400 flex items-center gap-1.5 mb-2"><SearchIcon size={13} /> Aperçu Google</span>
            <p className="font-sub text-[#1a0dab] text-lg leading-tight">{form.meta_title || form.title || "Titre de l'article"}</p>
            <p className="font-sub text-[#006621] text-sm">lecho-metropole.fr › {cat.slug} › {form.slug || "…"}</p>
            <p className="font-body text-sm text-slate-600 mt-0.5 line-clamp-2">{form.meta_description || form.summary || "Description de l'article…"}</p>
          </div>
        </div>
      )}
    </div>
  );
}
